import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  User,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Edit,
  Check,
  CreditCard,
  FileSignature,
  Calendar,
  Phone,
  Mail,
  School,
  Users,
  ArrowUpFromLine,
  ArrowDownToLine,
  Repeat,
  RefreshCw,
  DollarSign,
} from "lucide-react";
import { API } from "../config/api";

const formatEST = (dateString, formatStr = 'MMM d, yyyy h:mm a') => {
  if (!dateString) return '-';
  try {
    let normalizedDate = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      normalizedDate = dateString + 'Z';
    }
    const date = typeof normalizedDate === 'string' ? parseISO(normalizedDate) : normalizedDate;
    return formatInTimeZone(date, 'America/New_York', formatStr);
  } catch {
    return '-';
  }
};

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [stripeTransactions, setStripeTransactions] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // Charge fee dialog state
  const [showChargeDialog, setShowChargeDialog] = useState(false);
  const [chargeForm, setChargeForm] = useState({
    amount: '35.00',
    fee_type: 'Swap Fee',
    description: ''
  });
  const [charging, setCharging] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  // Fetch Stripe transactions when payments tab is active
  useEffect(() => {
    if (activeTab === 'payments' && userId && !stripeTransactions) {
      fetchStripeTransactions();
    }
  }, [activeTab, userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setUserData(response.data);
      setEditForm(response.data.user || {});
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/stripe-transactions`);
      setStripeTransactions(response.data);
    } catch (err) {
      console.error("Error fetching Stripe transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${userId}`, editForm);
      toast.success("User updated successfully");
      setEditing(false);
      fetchUserDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleChargeFee = async () => {
    if (!chargeForm.amount || parseFloat(chargeForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setCharging(true);
    try {
      const response = await axios.post(`${API}/users/${userId}/charge`, {
        amount: parseFloat(chargeForm.amount),
        fee_type: chargeForm.fee_type,
        description: chargeForm.description || `${chargeForm.fee_type} charge`
      });
      
      if (response.data.success) {
        toast.success(`Successfully charged $${parseFloat(chargeForm.amount).toFixed(2)}`);
        setShowChargeDialog(false);
        setChargeForm({ amount: '35.00', fee_type: 'Swap Fee', description: '' });
        // Refresh transactions to show the new charge
        fetchStripeTransactions();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to charge customer");
    } finally {
      setCharging(false);
    }
  };

  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  
  const handleGeneratePaymentLink = async () => {
    setGeneratingPaymentLink(true);
    try {
      const response = await axios.post(`${API}/users/${userId}/payment-method-link`);
      
      if (response.data.success && response.data.url) {
        // Try to copy to clipboard
        try {
          await navigator.clipboard.writeText(response.data.url);
          toast.success("Payment link copied to clipboard! You can send this to the customer.");
        } catch (clipboardErr) {
          // Clipboard failed - show the URL in a prompt or toast
          console.error("Clipboard error:", clipboardErr);
          toast.success(
            <div>
              <p className="font-medium">Payment link generated!</p>
              <p className="text-xs mt-1 break-all">{response.data.url}</p>
            </div>,
            { duration: 10000 }
          );
        }
      } else {
        toast.error("Failed to generate payment link - no URL returned");
      }
    } catch (err) {
      console.error("Payment link error:", err);
      toast.error(err.response?.data?.detail || "Failed to generate payment link");
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  const getRentalStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-emerald-100 text-emerald-700';
      case 'Completed': return 'bg-slate-100 text-slate-700';
      case 'Cancelled': return 'bg-red-100 text-red-700';
      case 'Awaiting Pickup': return 'bg-amber-100 text-amber-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <div className="flex gap-2">
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />Go Back
          </Button>
          <Button onClick={fetchUserDetails}>Retry</Button>
        </div>
      </div>
    );
  }

  const user = userData?.user;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">{user?.full_name || 'User Details'}</h1>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchUserDetails}>
                <RefreshCw className="w-4 h-4 mr-2" />Refresh
              </Button>
              {user?.stripe_customer_id && (
                <a 
                  href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center"
                >
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 cursor-pointer transition-colors">
                    <CreditCard className="w-3 h-3 mr-1" />
                    Stripe: {user.stripe_customer_id.slice(-8)}
                  </Badge>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1">
            {['overview', 'rentals', 'payments', 'contracts', 'history'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {tab === 'rentals' && userData?.rentals?.length > 0 && (
                  <span className="ml-2 text-xs bg-slate-200 px-2 py-0.5 rounded-full">{userData.rentals.length}</span>
                )}
                {tab === 'contracts' && userData?.signed_contracts?.length > 0 && (
                  <span className="ml-2 text-xs bg-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full">{userData.signed_contracts.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-3 gap-6">
            {/* Contact Info Card */}
            <div className="col-span-2 bg-white rounded-lg border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">Contact Information</h2>
                <Button variant="outline" size="sm" onClick={() => setEditing(!editing)}>
                  {editing ? 'Cancel' : <><Edit className="w-3 h-3 mr-1" />Edit</>}
                </Button>
              </div>
              
              {editing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Full Name</Label>
                    <Input 
                      value={editForm.full_name || ''} 
                      onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input 
                      value={editForm.email || ''} 
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input 
                      value={editForm.phone || ''} 
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date of Birth</Label>
                    <Input 
                      value={editForm.date_of_birth || ''} 
                      onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">School</Label>
                    <Input 
                      value={editForm.school || ''} 
                      onChange={(e) => setEditForm({...editForm, school: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Coach's Name</Label>
                    <Input 
                      value={editForm.coachs_name || ''} 
                      onChange={(e) => setEditForm({...editForm, coachs_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Coach's Phone</Label>
                    <Input 
                      value={editForm.coachs_phone_number || ''} 
                      onChange={(e) => setEditForm({...editForm, coachs_phone_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Coach's Email</Label>
                    <Input 
                      value={editForm.coachs_phone_email || ''} 
                      onChange={(e) => setEditForm({...editForm, coachs_phone_email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Guardian Name</Label>
                    <Input 
                      value={editForm.gaurdian_name || ''} 
                      onChange={(e) => setEditForm({...editForm, gaurdian_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Guardian Email</Label>
                    <Input 
                      value={editForm.gaurdian_email || ''} 
                      onChange={(e) => setEditForm({...editForm, gaurdian_email: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2 pt-4">
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-400">Phone</div>
                      <div className="font-medium">{user?.phone || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-400">Date of Birth</div>
                      <div className="font-medium">{user?.date_of_birth || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <School className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-400">School</div>
                      <div className="font-medium">{user?.school || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-400">Club</div>
                      <div className="font-medium">{user?.club_name || '-'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-400">Coach</div>
                      <div className="font-medium">{user?.coachs_name || '-'}</div>
                      {user?.coachs_phone_number && <div className="text-xs text-slate-500">{user.coachs_phone_number}</div>}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-400">Guardian</div>
                      <div className="font-medium">{user?.gaurdian_name || '-'}</div>
                      {user?.gaurdian_email && <div className="text-xs text-slate-500">{user.gaurdian_email}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stats Card */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Total Rentals</span>
                    <span className="text-2xl font-bold">{userData?.rentals?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Active Rentals</span>
                    <span className="text-2xl font-bold text-emerald-600">
                      {userData?.rentals?.filter(r => r.status === 'Active').length || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Signed Contracts</span>
                    <span className="text-2xl font-bold text-blue-600">{userData?.signed_contracts?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Payment Methods</span>
                    <span className="text-2xl font-bold text-purple-600">{userData?.payment_methods?.length || 0}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Member Info</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-slate-400">Member Since</div>
                    <div className="font-medium">{formatEST(user?.created_at, 'MMM d, yyyy')}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">Last Updated</div>
                    <div className="font-medium">{formatEST(user?.updated_at, 'MMM d, yyyy')}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">User ID</div>
                    <div className="font-mono">{user?.id}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rentals Tab */}
        {activeTab === 'rentals' && (
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Rental History</h2>
            </div>
            <div className="divide-y divide-slate-200">
              {userData?.rentals?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No rentals found</div>
              ) : (
                userData?.rentals?.map((rental) => (
                  <div key={rental.rental_id} className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="font-semibold text-lg">Rental #{rental.rental_id}</div>
                        <Badge className={getRentalStatusColor(rental.status)}>{rental.status}</Badge>
                      </div>
                      <div className="text-sm text-slate-500">
                        {formatEST(rental.start_date, 'MMM d, yyyy')}
                        {rental.return_date && ` → ${formatEST(rental.return_date, 'MMM d, yyyy')}`}
                      </div>
                    </div>
                    {rental.poles?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {rental.poles.map((pole, idx) => (
                          <div key={idx} className="bg-slate-100 px-4 py-2 rounded-lg">
                            <span className="font-medium">{pole.length} - {pole.weight}</span>
                            <span className="text-slate-500 ml-2">{pole.brand}</span>
                            <span className="text-slate-400 ml-2 text-sm">{pole.pole_code || pole.serial_num}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline"
                onClick={handleGeneratePaymentLink}
                disabled={generatingPaymentLink}
              >
                {generatingPaymentLink ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                {generatingPaymentLink ? 'Generating...' : 'Get Payment Link'}
              </Button>
              <Button 
                onClick={() => setShowChargeDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={!userData?.user?.stripe_customer_id}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Charge Fee
              </Button>
            </div>
            
            {/* Summary Row */}
            {stripeTransactions?.customer_id && (
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-sm text-slate-500 mb-1">Total Charged</div>
                  <div className="text-2xl font-bold text-slate-900">
                    ${stripeTransactions.total_charged?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-sm text-slate-500 mb-1">Total Refunded</div>
                  <div className="text-2xl font-bold text-red-600">
                    ${stripeTransactions.total_refunded?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-sm text-slate-500 mb-1">Transactions</div>
                  <div className="text-2xl font-bold text-slate-900">
                    {stripeTransactions.charges?.length || 0}
                  </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="text-sm text-slate-500 mb-1">Stripe Customer</div>
                  <a 
                    href={`https://dashboard.stripe.com/customers/${stripeTransactions.customer_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-mono underline"
                  >
                    {stripeTransactions.customer_id}
                  </a>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6">
              {/* Payment Methods */}
              <div className="bg-white rounded-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Payment Methods</h2>
                </div>
                <div className="p-6">
                  {userData?.payment_methods?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      No payment methods on file
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {userData?.payment_methods?.map((pm, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg">
                          <CreditCard className="w-8 h-8 text-slate-400" />
                          <div>
                            <div className="font-medium">{pm.brand || pm.card?.brand || 'Card'} •••• {pm.last4 || pm.card?.last4}</div>
                            <div className="text-sm text-slate-500">Expires {pm.exp_month || pm.card?.exp_month}/{pm.exp_year || pm.card?.exp_year}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Subscriptions */}
              <div className="bg-white rounded-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-lg font-semibold text-slate-900">Subscriptions</h2>
                </div>
                <div className="p-6">
                  {userData?.subscriptions?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">No active subscriptions</div>
                  ) : (
                    <div className="space-y-3">
                      {userData?.subscriptions?.map((sub) => (
                        <div key={sub.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                          <div>
                            <div className="font-medium">Rental #{sub.rental_id}</div>
                            <div className="text-xs text-slate-500 font-mono">{sub.stripe_subscription_id}</div>
                          </div>
                          <div className="text-right">
                            <Badge className={sub.subscribed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
                              {sub.subscribed ? 'Active' : 'Inactive'}
                            </Badge>
                            {sub.expire_at && <div className="text-xs text-slate-500 mt-1">{formatEST(sub.expire_at, 'MMM d, yyyy')}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stripe Transactions */}
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Stripe Transaction History</h2>
                <Button variant="outline" size="sm" onClick={fetchStripeTransactions} disabled={loadingTransactions}>
                  {loadingTransactions ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
              <div className="p-6">
                {loadingTransactions ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500">Loading transactions...</p>
                  </div>
                ) : !stripeTransactions?.customer_id ? (
                  <div className="text-center py-8 text-slate-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    No Stripe customer linked to this user
                  </div>
                ) : stripeTransactions.charges?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No transactions found
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {stripeTransactions.charges?.map((charge) => (
                        <div key={charge.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              charge.status === 'succeeded' ? 'bg-emerald-100' : 
                              charge.status === 'failed' ? 'bg-red-100' : 'bg-amber-100'
                            }`}>
                              <CreditCard className={`w-5 h-5 ${
                                charge.status === 'succeeded' ? 'text-emerald-600' : 
                                charge.status === 'failed' ? 'text-red-600' : 'text-amber-600'
                              }`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  ${charge.amount.toFixed(2)} {charge.currency}
                                </span>
                                <Badge className={
                                  charge.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' : 
                                  charge.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                }>
                                  {charge.status}
                                </Badge>
                                {charge.refunded && (
                                  <Badge className="bg-orange-100 text-orange-700">
                                    Refunded: ${charge.amount_refunded.toFixed(2)}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                                {charge.card_brand && <span className="capitalize">{charge.card_brand} •••• {charge.card_last4}</span>}
                                <span>•</span>
                                <span>{new Date(charge.created * 1000).toLocaleString()}</span>
                              </div>
                              {charge.description && (
                                <div className="text-xs text-slate-400 mt-1">{charge.description}</div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {charge.receipt_url && (
                              <a 
                                href={charge.receipt_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Receipt
                              </a>
                            )}
                            <a 
                              href={`https://dashboard.stripe.com/payments/${charge.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-slate-600 text-xs font-mono"
                            >
                              {charge.id.slice(-8)}
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contracts Tab */}
        {activeTab === 'contracts' && (
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Signed Contracts</h2>
              </div>
              <div className="p-6">
                {userData?.signed_contracts?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileSignature className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    No signed contracts found
                  </div>
                ) : (
                  <div className="space-y-3">
                    {userData?.signed_contracts?.map((contract, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <Check className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <div className="font-medium">Request #{contract.request_id}</div>
                            <div className="text-xs text-slate-500">Submission: {contract.submission_id}</div>
                          </div>
                        </div>
                        <div className="text-sm text-slate-600">
                          {contract.signed_at || 'Unknown date'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Pole Requests</h2>
              </div>
              <div className="p-6">
                {userData?.pole_requests?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No pole requests</div>
                ) : (
                  <div className="space-y-2">
                    {userData?.pole_requests?.map((req) => (
                      <div key={req.request_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="font-medium">Request #{req.request_id}</span>
                          <Badge variant="outline" className="ml-2">{req.status}</Badge>
                        </div>
                        <div className="text-xs text-slate-500">{formatEST(req.created_at, 'MMM d, yyyy')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Pickup/Checkout History</h2>
            </div>
            <div className="divide-y divide-slate-200">
              {userData?.pickup_returns?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">No pickup/checkout history</div>
              ) : (
                userData?.pickup_returns?.map((pr) => (
                  <div key={pr.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      {pr.status === 0 ? (
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <ArrowUpFromLine className="w-5 h-5 text-blue-600" />
                        </div>
                      ) : pr.swap ? (
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <Repeat className="w-5 h-5 text-purple-600" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <ArrowDownToLine className="w-5 h-5 text-green-600" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{pr.pole_length} - {pr.pole_weight}</div>
                        <div className="text-sm text-slate-500">{pr.pole_code || pr.serial_num}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={pr.status === 0 ? 'bg-blue-100 text-blue-700' : pr.swap ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}>
                        {pr.status === 0 ? 'Checkout' : pr.swap ? 'Swap' : 'Pickup'}
                      </Badge>
                      <div className="text-sm text-slate-500 mt-1">{formatEST(pr.created_at, 'MMM d, yyyy')}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Charge Fee Dialog */}
      <Dialog open={showChargeDialog} onOpenChange={setShowChargeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Charge Fee</DialogTitle>
            <DialogDescription>
              Charge a one-time fee to {userData?.user?.full_name || 'this customer'}'s account. 
              This will be billed immediately to their payment method on file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Fee Type</Label>
              <Select
                value={chargeForm.fee_type}
                onValueChange={(val) => {
                  setChargeForm({ ...chargeForm, fee_type: val });
                  // Auto-fill amount for common fee types
                  if (val === 'Swap Fee') setChargeForm(prev => ({ ...prev, fee_type: val, amount: '35.00' }));
                  else if (val === 'Late Fee') setChargeForm(prev => ({ ...prev, fee_type: val, amount: '25.00' }));
                  else if (val === 'Damage Fee') setChargeForm(prev => ({ ...prev, fee_type: val, amount: '100.00' }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Swap Fee">Swap Fee</SelectItem>
                  <SelectItem value="Late Fee">Late Fee</SelectItem>
                  <SelectItem value="Damage Fee">Damage Fee</SelectItem>
                  <SelectItem value="Processing Fee">Processing Fee</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={chargeForm.amount}
                onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="e.g., Swap fee for pole exchange"
                value={chargeForm.description}
                onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChargeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleChargeFee} 
              disabled={charging || !chargeForm.amount}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {charging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Charging...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Charge ${parseFloat(chargeForm.amount || 0).toFixed(2)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
